import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { BookOpenIcon } from 'lucide-react';
/**
 * DragDropFillRenderer — Fill blanks by clicking drag options.
 *
 * Supports two seed data formats:
 * Format A (canonical): template with ___, blanks[{id, correctAnswer}], dragOptions[]
 * Format B (legacy):    template with {0}/{1}, draggables[], correctPlacements{}
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { DRAG_DROP_FILL_MODES, readMode } from '../../../content/questions/questionModes';
import { hapticManager } from '../../../utils/hapticManager';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
interface NormalizedBlank {
  id: string;
  correctAnswer: string;
}
/** Normalize both seed formats into a canonical shape */
function normalizeConfig(config: Record<string, unknown>): {
  parts: string[];
  blanks: NormalizedBlank[];
  dragOptions: string[];
} {
  const template: string = config.template as string || '';
  const rawBlanks = config.blanks as Array<{
    id: string;
    correctAnswer: string;
  }> | undefined;
  const draggables = config.draggables as string[] | undefined;
  const correctPlacements = config.correctPlacements as Record<string, number> | undefined;
  // Detect format: if draggables + correctPlacements exist, it's Format B ({0}/{1} placeholders)
  if (draggables && correctPlacements) {
    // Format B: template uses {0}, {1}, etc.
    const placeholderRegex = /\{(\d+)\}/g;
    const ids: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = placeholderRegex.exec(template)) !== null) {
      ids.push(match[1]);
    }
    const parts = template.split(/\{\d+\}/);
    const blanks: NormalizedBlank[] = ids.map(id => ({
      id,
      correctAnswer: draggables[correctPlacements[id] ?? 0] || ''
    }));
    return {
      parts,
      blanks,
      dragOptions: draggables
    };
  }
  // Format A: template uses ___
  const parts = template.split('___');
  const blanks: NormalizedBlank[] = rawBlanks || [];
  // dragOptions can be in config.dragOptions or config.options (some seeds use data.options)
  const dragOptions: string[] = config.dragOptions as string[] || config.options as string[] || [];
  return {
    parts,
    blanks,
    dragOptions
  };
}
export function DragDropFillRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = (question.data || {}) as Record<string, unknown>;
  const {
    parts,
    blanks,
    dragOptions
  } = normalizeConfig(config);
  const mode = readMode(question, DRAG_DROP_FILL_MODES);
  const isWithBank = mode === 'with_bank';
  const [filledBlanks, setFilledBlanks] = useState<Record<string, string>>({});
  useEffect(() => {
    setFilledBlanks({});
  }, [question.id]);
  useEffect(() => {
    const isReady = blanks.every(b => filledBlanks[b.id]);
    onReadyChange?.(isReady);
  }, [filledBlanks, blanks, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(filledBlanks), [filledBlanks]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handleOptionClick = (option: string) => {
    const firstEmpty = blanks.find(b => !filledBlanks[b.id]);
    if (firstEmpty) {
      // F-FEEL-2 follow-up: tactile pickup-feel on placing a word
      hapticManager.hapticSelection();
      setFilledBlanks(prev => ({
        ...prev,
        [firstEmpty.id]: option
      }));
    }
  };
  const handleBlankClick = (blankId: string) => {
    // F-FEEL-2 follow-up: tactile feel on clearing a filled blank
    hapticManager.hapticLight();
    const next = {
      ...filledBlanks
    };
    delete next[blankId];
    setFilledBlanks(next);
  };
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      document.getElementById(`drag-option-${(index + 1) % dragOptions.length}`)?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      document.getElementById(`drag-option-${(index - 1 + dragOptions.length) % dragOptions.length}`)?.focus();
    }
  };
  return <div className="w-full max-w-2xl mx-auto flex flex-col gap-5 sm:gap-8" data-id="element-11351">
      {/* Sentence Area */}
      <div className="p-4 sm:p-6 bg-indigo-50 rounded-2xl border-2 border-indigo-100 text-base sm:text-xl leading-relaxed sm:leading-loose flex flex-wrap items-center gap-1.5 sm:gap-2 justify-center" data-id="element-11352">
        {parts.map((part: string, index: number) => <Fragment key={index} data-id="element-11353">
            <span data-id="element-11354">
              <LatexRenderer content={part} data-id="element-11355" />
            </span>
            {index < parts.length - 1 && blanks[index] && <button onClick={() => handleBlankClick(blanks[index].id)} aria-label={`Lege plek ${index + 1}${filledBlanks[blanks[index].id] ? `, gevuld met ${filledBlanks[blanks[index].id]}` : ', leeg'}`} className={`min-w-[80px] sm:min-w-[100px] min-h-[44px] h-10 sm:h-11 px-3 rounded-lg border-2 border-dashed transition-all flex items-center justify-center font-bold text-sm sm:text-base touch-manipulation focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 active:scale-95 ${filledBlanks[blanks[index].id] ? 'bg-white border-indigo-500 text-indigo-700 shadow-sm' : 'bg-white/50 border-slate-300 text-slate-400'}`} data-id="element-11356">
                {filledBlanks[blanks[index].id] || '?'}
              </button>}
          </Fragment>)}
      </div>

      {/* Options Area — wrapped in a styled "woordbank" panel in with_bank mode */}
      {isWithBank && <div className="flex items-center gap-2 -mb-1 px-1" data-id="element-11357">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-amber-100 text-amber-700" aria-hidden="true" data-id="element-11358">
            <BookOpenIcon className="w-3.5 h-3.5" strokeWidth={2.25} data-id="element-11359" />
          </span>
          <span className="text-sm font-semibold text-slate-700" data-id="element-11360">
            Woordbank
          </span>
          <span className="text-xs text-slate-400" data-id="element-11361">
            (tik een woord om te plaatsen)
          </span>
        </div>}
      <div className={isWithBank ? 'flex flex-wrap gap-2.5 sm:gap-3 justify-center px-3 py-4 sm:py-5 bg-amber-50/60 border-2 border-amber-200 border-dashed rounded-2xl' : 'flex flex-wrap gap-2.5 sm:gap-3 justify-center px-1'} role="listbox" aria-label={isWithBank ? 'Woordbank — kies een woord om in te vullen' : 'Kies een woord om in te vullen'} data-id="element-11362">
        {dragOptions.map((option: string, index: number) => {
        const isUsed = Object.values(filledBlanks).includes(option);
        return <button key={index} id={`drag-option-${index}`} role="option" aria-selected={isUsed} onClick={() => !isUsed && handleOptionClick(option)} onKeyDown={e => handleKeyDown(e, index)} disabled={isUsed} className={`px-3.5 sm:px-4 py-2.5 sm:py-2 min-h-[44px] rounded-xl border-2 font-medium text-base sm:text-lg transition-all touch-manipulation focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${isUsed ? 'bg-slate-100 border-slate-200 text-slate-300 cursor-default' : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-400 hover:shadow-md active:scale-95 sm:hover:-translate-y-0.5'}`} data-id="element-11363">
              <LatexRenderer content={option} data-id="element-11364" />
            </button>;
      })}
      </div>

      <p className="text-center text-slate-500 text-xs sm:text-sm mt-2 sm:mt-4" data-id="element-11365">
        Tik op een woord om het te plaatsen
      </p>
    </div>;
}